import {
  FRAME_HEADER_HEIGHT,
  hitTestNode,
  hitTestPoint,
  hitTestRect,
  nodeAABB,
  rectExpand,
  rectIntersects,
  resolveSelectionTarget,
  vDist,
  visibleRect,
  type BoardNode,
  type NodeId,
  type Rect,
  type Vec,
  type Viewport,
} from '@kanvas/board-core';
import type { BoardDoc } from '../collab/BoardDoc';
import type { AnchorHint } from '../state/scratch';

/** Pick radius in screen pixels, converted to board units by the caller. */
export const PICK_SLOP_PX = 5;
export const ANCHOR_SLOP_PX = 12;

export function slop(vp: Viewport, px = PICK_SLOP_PX): number {
  return px / vp.zoom;
}

export interface PickOptions {
  vp: Viewport;
  /** Groups the user has stepped into, so their children are directly pickable. */
  enteredGroups?: ReadonlySet<NodeId>;
  /** Frames only respond to their header and border unless this is false. */
  frameEdgeOnly?: boolean;
  includeLocked?: boolean;
}

/** Topmost node under a board point, resolved through group membership. */
export function pickNode(
  doc: BoardDoc,
  board: Vec,
  options: PickOptions,
): BoardNode | null {
  const hit = hitTestPoint(doc.all(), board, {
    lookup: doc.lookup,
    tolerance: slop(options.vp),
    frameEdgeOnly: options.frameEdgeOnly ?? true,
    includeLocked: options.includeLocked ?? false,
    // The frame label is painted in screen pixels, so its hit strip must be
    // too — a board-unit strip is 4× too tall at 400% and vanishes at 25%.
    frameHeaderHeight: FRAME_HEADER_HEIGHT / options.vp.zoom,
  });
  if (!hit) return null;
  return resolveSelectionTarget(hit, doc.lookup, options.enteredGroups ?? new Set());
}

/** Node under the point ignoring group resolution, for double-click drill-in. */
export function pickLeaf(doc: BoardDoc, board: Vec, options: PickOptions): BoardNode | null {
  return hitTestPoint(doc.all(), board, {
    lookup: doc.lookup,
    tolerance: slop(options.vp),
    frameEdgeOnly: false,
    includeLocked: options.includeLocked ?? false,
    frameHeaderHeight: FRAME_HEADER_HEIGHT / options.vp.zoom,
  });
}

export function pickInRect(
  doc: BoardDoc,
  area: Rect,
  options: PickOptions & { mode?: 'intersect' | 'contain' },
): BoardNode[] {
  const hits = hitTestRect(doc.all(), area, {
    lookup: doc.lookup,
    mode: options.mode ?? 'intersect',
    includeLocked: options.includeLocked ?? false,
  });

  // A marquee selects group roots, never individual members.
  const resolved = new Map<NodeId, BoardNode>();
  for (const hit of hits) {
    const target = resolveSelectionTarget(hit, doc.lookup, options.enteredGroups ?? new Set());
    resolved.set(target.id, target);
  }
  return [...resolved.values()];
}

/**
 * Connector anchor near the pointer.
 *
 * Anchors are the four edge midpoints. Snapping to them (rather than always
 * using an auto anchor) is what lets a user pin a line to a specific side of a
 * box and have it stay there.
 */
export function pickAnchor(
  doc: BoardDoc,
  board: Vec,
  vp: Viewport,
  excludeIds: ReadonlySet<NodeId> = new Set(),
): AnchorHint | null {
  const reach = slop(vp, ANCHOR_SLOP_PX);
  const near = rectExpand(visibleRect(vp), reach * 4);

  let best: { hint: AnchorHint; d: number } | null = null;

  for (const node of doc.all()) {
    if (node.type === 'connector' || node.type === 'group' || node.locked) continue;
    // Pins are a dot, not a box with sides; hidden nodes are not on screen.
    if (node.type === 'comment' || node.hidden) continue;
    if (excludeIds.has(node.id)) continue;
    if (!rectIntersects(near, nodeAABB(node, doc.lookup))) continue;

    const candidates: [number, number][] = [
      [0.5, 0],
      [1, 0.5],
      [0.5, 1],
      [0, 0.5],
    ];
    for (const [nx, ny] of candidates) {
      const point = { x: node.x + node.w * nx, y: node.y + node.h * ny };
      const d = vDist(point, board);
      if (d > reach) continue;
      if (best && d >= best.d) continue;
      best = { hint: { nodeId: node.id, nx, ny, point }, d };
    }
  }
  return best?.hint ?? null;
}

/** Node a loose connector end would attach to. */
export function pickConnectorTarget(
  doc: BoardDoc,
  board: Vec,
  vp: Viewport,
  excludeIds: ReadonlySet<NodeId> = new Set(),
): BoardNode | null {
  for (const node of [...doc.all()].reverse()) {
    if (node.type === 'connector' || node.type === 'group' || node.locked) continue;
    if (excludeIds.has(node.id)) continue;
    if (
      hitTestNode(node, board, {
        lookup: doc.lookup,
        tolerance: slop(vp),
        frameEdgeOnly: false,
      })
    ) {
      return node;
    }
  }
  return null;
}

/** Frame whose header strip is under the pointer, for dragging by the title. */
export function pickFrameHeader(doc: BoardDoc, board: Vec, vp: Viewport): BoardNode | null {
  const pad = slop(vp, 2);
  for (const node of [...doc.all()].reverse()) {
    if (node.type !== 'frame' || node.hidden) continue;
    const header: Rect = {
      x: node.x - pad,
      y: node.y - FRAME_HEADER_HEIGHT / vp.zoom - pad,
      w: node.w + pad * 2,
      h: FRAME_HEADER_HEIGHT / vp.zoom + pad * 2,
    };
    if (
      board.x >= header.x &&
      board.x <= header.x + header.w &&
      board.y >= header.y &&
      board.y <= header.y + header.h
    ) {
      return node;
    }
  }
  return null;
}

/**
 * Snap candidates for a move or resize: the AABBs of everything on screen that
 * is not being moved. Restricting to the viewport keeps the cost bounded on
 * large boards, and off-screen guides would be invisible anyway.
 */
export function snapCandidates(
  doc: BoardDoc,
  vp: Viewport,
  exclude: ReadonlySet<NodeId>,
  limit = 400,
): Rect[] {
  const area = rectExpand(visibleRect(vp), 200 / vp.zoom);
  const out: Rect[] = [];
  for (const node of doc.all()) {
    if (exclude.has(node.id)) continue;
    if (node.type === 'group' || node.type === 'connector') continue;
    const aabb = nodeAABB(node, doc.lookup);
    if (!rectIntersects(area, aabb)) continue;
    out.push(aabb);
    if (out.length >= limit) break;
  }
  return out;
}
